import { useEffect, useState } from "react";
import { MapContainer, Marker, TileLayer, useMap, useMapEvents } from "react-leaflet";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { DEFAULT_MAP_CENTER, DEFAULT_MAP_ZOOM } from "@/lib/leaflet-setup";

const PICKED_ZOOM = 12;

interface GeocodeResult {
  address: string | null;
  city: string;
  country: string;
}

async function reverseGeocode(lat: number, lng: number): Promise<GeocodeResult> {
  try {
    const response = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`);
    const data = await response.json();
    if (!data.address) return { address: null, city: "", country: "" };
    const city = data.address.city || data.address.town || data.address.village || "";
    const country = data.address.country || "";
    const parts = [city, data.address.state, country].filter(Boolean);
    return { address: parts.length > 0 ? parts.join(", ") : null, city, country };
  } catch {
    return { address: null, city: "", country: "" };
  }
}

interface AddressSuggestion {
  lat: number;
  lng: number;
  name: string;
}

async function forwardGeocode(address: string): Promise<AddressSuggestion[]> {
  if (!address || address.trim().length < 2) return [];
  try {
    const response = await fetch(
      `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(address)}&limit=5`
    );
    const data = await response.json();
    return data.map((result: { lat: string; lon: string; display_name: string }) => ({
      lat: parseFloat(result.lat),
      lng: parseFloat(result.lon),
      name: result.display_name,
    }));
  } catch {
    return [];
  }
}

function MapClickHandler({ onPick }: { onPick: (pos: { lat: number; lng: number }) => void }) {
  const map = useMapEvents({
    click(e) {
      onPick({ lat: e.latlng.lat, lng: e.latlng.lng });
    },
  });

  // Leaflet computes a stale size when its container mounts inside a Dialog
  // whose open-animation layout isn't finalized yet — recompute after it
  // settles. Same fix the sibling platform uses.
  useEffect(() => {
    const timer = setTimeout(() => map.invalidateSize(), 300);
    return () => clearTimeout(timer);
  }, [map]);

  return null;
}

function MapRecenter({ position }: { position: { lat: number; lng: number } | null }) {
  const map = useMap();
  useEffect(() => {
    if (position) {
      map.setView([position.lat, position.lng], Math.max(map.getZoom(), PICKED_ZOOM));
    }
    // Only re-run when the coordinates themselves change, not on every
    // MapRecenter/map re-render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [position?.lat, position?.lng]);
  return null;
}

export interface LocationPickerResult {
  address: string;
  latitude: string;
  longitude: string;
  city: string;
  country: string;
}

interface LocationPickerProps {
  initialPosition?: { lat: number; lng: number } | null;
  onLocationSelected: (fields: LocationPickerResult) => void;
}

function LocationPicker({ initialPosition, onLocationSelected }: LocationPickerProps) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([]);
  const [markerPosition, setMarkerPosition] = useState<{ lat: number; lng: number } | null>(null);
  const [resolved, setResolved] = useState<GeocodeResult>({ address: null, city: "", country: "" });

  // Reset to the asset's current coordinates (or empty) each time the dialog opens.
  useEffect(() => {
    if (dialogOpen) {
      setMarkerPosition(initialPosition ?? null);
      setSearchQuery("");
      setSuggestions([]);
      setResolved({ address: null, city: "", country: "" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dialogOpen]);

  // Debounced address search — 500ms, matching the sibling platform's timing.
  useEffect(() => {
    if (!dialogOpen || searchQuery.trim().length < 2) {
      setSuggestions([]);
      return;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      const results = await forwardGeocode(searchQuery);
      if (!cancelled) setSuggestions(results);
    }, 500);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [searchQuery, dialogOpen]);

  // Reverse-geocode whenever the marker moves (from a map click or a
  // suggestion pick) — single source of truth for address/city/country,
  // instead of duplicating the geocode call in both the click handler and
  // the suggestion-select handler.
  useEffect(() => {
    if (!markerPosition) return;
    let cancelled = false;
    reverseGeocode(markerPosition.lat, markerPosition.lng).then((result) => {
      if (!cancelled) setResolved(result);
    });
    return () => {
      cancelled = true;
    };
  }, [markerPosition?.lat, markerPosition?.lng]);

  function handleSuggestionClick(suggestion: AddressSuggestion) {
    setMarkerPosition({ lat: suggestion.lat, lng: suggestion.lng });
    setSuggestions([]);
    setSearchQuery(suggestion.name);
  }

  function handleUseLocation() {
    if (!markerPosition) return;
    onLocationSelected({
      address: resolved.address ?? "",
      latitude: String(markerPosition.lat),
      longitude: String(markerPosition.lng),
      city: resolved.city,
      country: resolved.country,
    });
    setDialogOpen(false);
  }

  return (
    <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
      <DialogTrigger asChild>
        <Button type="button" variant="outline">
          Pick on map
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Pick a location</DialogTitle>
          <DialogDescription>Search for an address or click on the map to drop a pin.</DialogDescription>
        </DialogHeader>

        <div className="relative">
          <Input
            placeholder="Search for an address..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            autoComplete="off"
          />
          {suggestions.length > 0 && (
            <div className="absolute left-0 right-0 top-full z-50 mt-1 max-h-60 overflow-y-auto rounded-md border bg-popover shadow-md">
              {suggestions.map((suggestion, index) => (
                <button
                  key={index}
                  type="button"
                  className="block w-full px-3 py-2 text-left text-sm hover:bg-muted"
                  onClick={() => handleSuggestionClick(suggestion)}
                >
                  {suggestion.name}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="h-80 w-full overflow-hidden rounded-md border">
          <MapContainer
            center={initialPosition ? [initialPosition.lat, initialPosition.lng] : DEFAULT_MAP_CENTER}
            zoom={initialPosition ? PICKED_ZOOM : DEFAULT_MAP_ZOOM}
            style={{ height: "100%", width: "100%" }}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <MapClickHandler onPick={setMarkerPosition} />
            <MapRecenter position={markerPosition} />
            {markerPosition && <Marker position={[markerPosition.lat, markerPosition.lng]} />}
          </MapContainer>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
            Cancel
          </Button>
          <Button type="button" onClick={handleUseLocation} disabled={!markerPosition}>
            Use this location
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default LocationPicker;
